import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONTS, LAYOUT, SPACING } from '../constants/theme';
import { UIButton } from './UIButton';
import type { LayoutMetrics } from '../LayoutManager';

export interface WaitingOverlayOptions {
  title: string;
  message: string;
  onCancel?: () => void;
}

/**
 * Overlay shown when waiting for opponent in online mode
 */
export class WaitingOverlay extends Container {
  private options: WaitingOverlayOptions;
  private background: Graphics;
  private panel: Container;
  private panelBg: Graphics;
  private titleText: Text;
  private messageText: Text;
  private spinnerContainer: Container;
  private cancelButton: UIButton | null = null;

  constructor(metrics: LayoutMetrics, options: WaitingOverlayOptions) {
    super();
    this.options = options;

    // Light semi-transparent background - less intrusive
    this.background = new Graphics();
    this.background.rect(0, 0, metrics.width, metrics.height);
    this.background.fill({ color: 0x000000, alpha: 0.3 });
    this.background.eventMode = 'static';
    this.addChild(this.background);

    // Panel - compact design
    this.panel = new Container();
    this.panel.eventMode = 'static';
    this.addChild(this.panel);

    const panelWidth = Math.min(280, metrics.width - SPACING.LG * 2);
    const panelHeight = options.onCancel ? 120 : 70;

    this.panelBg = new Graphics();
    this.panelBg.roundRect(0, 0, panelWidth, panelHeight, LAYOUT.BORDER_RADIUS);
    this.panelBg.fill({ color: COLORS.BG_SECONDARY, alpha: 0.95 });
    this.panelBg.stroke({ color: COLORS.PLAYER1, width: 2 });
    this.panelBg.eventMode = 'static';
    this.panel.addChild(this.panelBg);

    // Title and dots on same line
    const titleStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.MD,
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });
    this.titleText = new Text({
      text: options.title,
      style: titleStyle,
    });
    this.titleText.anchor.set(0.5, 0.5);
    this.titleText.x = panelWidth / 2;
    this.titleText.y = options.onCancel ? panelHeight / 2 - 20 : panelHeight / 2;
    this.panel.addChild(this.titleText);

    // Message text (hidden - not needed for compact design)
    const messageStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.SM,
      fill: COLORS.TEXT_SECONDARY,
    });
    this.messageText = new Text({
      text: options.message,
      style: messageStyle,
    });
    this.messageText.anchor.set(0.5, 0.5);
    this.messageText.x = panelWidth / 2;
    this.messageText.y = panelHeight / 2;
    this.messageText.visible = false; // Hidden in compact mode
    this.panel.addChild(this.messageText);

    // Spinner (3 animated dots) - inline after title
    this.spinnerContainer = new Container();
    this.spinnerContainer.x = panelWidth / 2 + this.titleText.width / 2 + 20;
    this.spinnerContainer.y = options.onCancel ? panelHeight / 2 - 20 : panelHeight / 2;
    this.panel.addChild(this.spinnerContainer);

    const dotRadius = 4;
    const dotGap = 12;
    for (let i = 0; i < 3; i++) {
      const dot = new Graphics();
      dot.circle(0, 0, dotRadius);
      dot.fill({ color: COLORS.PLAYER1 });
      dot.x = i * dotGap;
      this.spinnerContainer.addChild(dot);

      // Animate each dot with a staggered delay
      gsap.to(dot.scale, {
        x: 1.4,
        y: 1.4,
        duration: 0.5,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: i * 0.15,
      });
      gsap.to(dot, {
        alpha: 0.4,
        duration: 0.5,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: i * 0.15,
      });
    }

    // Cancel button (optional)
    if (options.onCancel) {
      this.cancelButton = new UIButton({
        text: 'Cancel',
        width: 100,
        height: 32,
        variant: 'secondary',
        onClick: () => this.handleCancel(),
      });
      this.cancelButton.x = panelWidth / 2;
      this.cancelButton.y = panelHeight - SPACING.MD - 16;
      this.panel.addChild(this.cancelButton);
    }

    // Position panel in center
    this.panel.x = (metrics.width - panelWidth) / 2;
    this.panel.y = (metrics.height - panelHeight) / 2;

    // Animate in
    this.alpha = 0;
    gsap.to(this, { alpha: 1, duration: 0.2 });
  }

  private handleCancel(): void {
    this.hide();
    this.options.onCancel?.();
  }

  /**
   * Update the message text
   */
  setMessage(message: string): void {
    this.messageText.text = message;
  }

  /**
   * Hide and destroy the overlay
   */
  hide(): void {
    gsap.killTweensOf(this.spinnerContainer.children);
    gsap.to(this, {
      alpha: 0,
      duration: 0.2,
      onComplete: () => {
        this.destroy();
      },
    });
  }

  /**
   * Update layout on resize
   */
  onResize(metrics: LayoutMetrics): void {
    // Update background
    this.background.clear();
    this.background.rect(0, 0, metrics.width, metrics.height);
    this.background.fill({ color: 0x000000, alpha: 0.3 });

    // Recalculate panel position
    const panelWidth = Math.min(280, metrics.width - SPACING.LG * 2);
    const panelHeight = this.options.onCancel ? 120 : 70;
    this.panel.x = (metrics.width - panelWidth) / 2;
    this.panel.y = (metrics.height - panelHeight) / 2;
  }

  /**
   * Clean up when destroyed
   */
  destroy(): void {
    gsap.killTweensOf(this.spinnerContainer.children);
    super.destroy();
  }
}
